// ============================================
// MATRIX VIEW - Correlation matrix (7x7)
// ============================================

import React, { useMemo } from 'react';
import type { Employee } from '../../types/results';
import { getHebrewName } from '../../constants/muscles';
import { calculateCorrelation } from '../../services/analytics';
import type { DemographicDimension } from '../../constants/demographics';
import { groupEmployeesByDemographic, getSegmentLabel } from '../../services/demographics';

interface MatrixViewProps {
    employees: Employee[];
    onPairSelect: (pair: [string, string]) => void;
    demographicDimension?: DemographicDimension | null;
    demographicSegmentKey?: string | null;
}

function getCellColor(val: number): string {
    // Brand colors: positive -> Primary Teal (#133437), negative -> Accent Gold (#baa37e)
    const alpha = Math.abs(val);
    if (val > 0) return `rgba(19, 52, 55, ${alpha})`; // Primary Teal
    return `rgba(186, 163, 126, ${alpha})`; // Accent Gold
}

export default function MatrixView({ employees, onPairSelect, demographicDimension, demographicSegmentKey }: MatrixViewProps) {
    // Optionally filter employees by demographic segment
    const filteredEmployees = useMemo(() => {
        if (!demographicDimension || !demographicSegmentKey) return employees;
        const groups = groupEmployeesByDemographic(employees, demographicDimension);
        return groups.get(demographicSegmentKey) || employees;
    }, [employees, demographicDimension, demographicSegmentKey]);

    const categories = useMemo(() => {
        if (filteredEmployees.length === 0) return [];
        return Object.keys(filteredEmployees[0].scores);
    }, [filteredEmployees]);

    const matrix = useMemo(() => {
        return categories.map(rowCat => {
            return categories.map(colCat => {
                const x = filteredEmployees.map(e => e.scores[rowCat] ?? 0);
                const y = filteredEmployees.map(e => e.scores[colCat] ?? 0);
                return {
                    row: rowCat,
                    col: colCat,
                    value: calculateCorrelation(x, y)
                };
            });
        });
    }, [filteredEmployees, categories]);

    if (filteredEmployees.length === 0 || categories.length === 0) {
        return (
            <div className="analytics-empty-state">
                <p>אין מספיק נתונים להצגת מטריצת מתאמים</p>
            </div>
        );
    }

    const segmentLabel = demographicSegmentKey && demographicDimension
        ? ` - ${getSegmentLabel(demographicSegmentKey, demographicDimension)}`
        : '';

    return (
        <div className="analytics-matrix-container" style={{ direction: 'ltr' }}>
            <h4 className="analytics-matrix-title">
                מטריצת מתאמים ({categories.length}x{categories.length}){segmentLabel} - לחץ על תא לניתוח עומק
            </h4>

            <div className="analytics-matrix-scroll">
                <div
                    className="analytics-matrix-grid"
                    style={{
                        gridTemplateColumns: `minmax(80px, 120px) repeat(${categories.length}, 1fr)`
                    }}
                >
                    {/* Top Left Corner (Empty) */}
                    <div></div>

                    {/* Top Labels */}
                    {categories.map((cat, i) => (
                        <div key={i} className="analytics-matrix-header-cell">
                            <span className="analytics-matrix-label">
                                {getHebrewName(cat)}
                            </span>
                        </div>
                    ))}

                    {/* Rows */}
                    {matrix.map((row, rIdx) => (
                        <React.Fragment key={`row-${rIdx}`}>
                            {/* Row Label */}
                            <div className="analytics-matrix-row-label">
                                <span>{getHebrewName(categories[rIdx])}</span>
                            </div>

                            {/* Cells */}
                            {row.map((cell, cIdx) => {
                                const isDark = cell.value > 0.5; // High positive correlations use dark teal
                                return (
                                    <div
                                        key={`cell-${rIdx}-${cIdx}`}
                                        className="analytics-matrix-cell"
                                        style={{
                                            backgroundColor: getCellColor(cell.value),
                                            color: isDark ? 'var(--neutral-50)' : 'var(--neutral-900)',
                                            fontWeight: Math.abs(cell.value) > 0.4 ? 700 : 400
                                        }}
                                        onClick={() => onPairSelect([cell.row, cell.col])}
                                        title={`${getHebrewName(cell.row)} vs ${getHebrewName(cell.col)}: ${cell.value.toFixed(2)}\nלחץ לצפייה בפיזור`}
                                    >
                                        <span style={{ fontSize: '11px' }}>
                                            {cell.value.toFixed(2)}
                                        </span>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}
